// THE PAGE KIT — the React side of attest.css §7, the one vocabulary every page dresses in.
//
// A dozen pages are built by a dozen hands; this file is where the shared dress is registered
// ONCE, so no page invents its own masthead, its own rail or its own table. If a page needs a
// variant, it goes HERE or in attest.css — never in a per-page <style> block.
//
//   PageShell  the page container: same max-width, same padding on every tab
//   Mast       the masthead: kicker, h1, sub-line, verbs. At most ONE .btn-accent in verbs.
//   Rail       the instrument: EXACTLY five cells on a sheet riding the masthead rule
//   RailCell   one cell: label (+ go hint), figure (+ unit), detail line. `.fd` is never blank.
//   Zone       a section in the page rhythm; ZoneHead its tracked label on a rule
//   Sheet      the bordered white container — content sits on sheets, never on the field
//   Tbl        table.tbl
//   FilterSelect one labelled select on a board's control bar — a cell filter, not a chip
//   Tag        .tag with the four tones
//   Att        the claim mark: ◇ proposed · ▪ held · ❝ quoted
//   Ev         blockquote.ev + .ev-src — a verbatim quote with its source
//   Lnk        a text verb
//   Fig        a quantity: date, count, money — tabular sans, NEVER mono
//   Ref        a human reference (ORG-0012) — mono, an identity, never a hue
//
// THE LAWS THIS FILE ENCODES (BUILD-BRIEF §2, ATTEST):
//   · Tone is text colour on the figure, never a hue assigned to a category. There is no
//     stage spectrum and there are no stat cards.
//   · A quantity is `Fig`; a reference is `Ref`. Never mono on a date or a count.
//   · A claim carries its mark: `att--proposed` for anything an agent or an import asserted
//     and nobody has held; `att--held` for what an operator committed to. A COMPUTED count
//     carries NO mark — it is not a claim, it is arithmetic.
//   · `unknown` is not `nil`. `nil` means counted and zero; `unknown` means nobody collected
//     it. They render differently on purpose.
import type { CSSProperties, HTMLAttributes, ReactNode, TableHTMLAttributes } from 'react';
// the shared vocabulary — PageKit is in every bundle, so registering it here gives every page
// the common words without each page importing them
import '../i18n/common.de.js';
import '../i18n/common.fr.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── the shell ───────────────────────────────────────────────────────────────
export function PageShell({
  className, children, ...rest
}: { className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  return <div className={cx('page', className)} {...rest}>{children}</div>;
}

// ── the masthead ────────────────────────────────────────────────────────────
export function Mast({
  title, sub, verbs, kicker, children, className,
}: {
  title: ReactNode;
  /** the meta line under the title — compose the facts with <Sep /> between them */
  sub?: ReactNode;
  /** the right-hand controls. At most ONE may be `.btn.btn-accent`: the page's single commit. */
  verbs?: ReactNode;
  /** the optional tracked label ABOVE the title */
  kicker?: ReactNode;
  /** extra masthead rows, inside the rule */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cx('mast', className)}>
      <div className="mast-top">
        <div className="mast-main">
          {kicker != null && <div className="u-label">{kicker}</div>}
          <h1>{title}</h1>
          {sub != null && <div className="mast-sub">{sub}</div>}
        </div>
        {verbs != null && <div className="mast-verbs">{verbs}</div>}
      </div>
      {children}
    </header>
  );
}

/** The quiet separator between masthead / zone-head facts. */
export function Sep() {
  return <span className="sep">·</span>;
}

// ── zones ───────────────────────────────────────────────────────────────────
export function Zone({
  id, className, children, ...rest
}: { id?: string; className?: string; children: ReactNode } & Omit<HTMLAttributes<HTMLElement>, 'className' | 'children' | 'id'>) {
  return <section className={cx('zone', className)} id={id} {...rest}>{children}</section>;
}

export function ZoneHead({
  label, count, note, endNote, route, children, className,
}: {
  label: ReactNode;
  /** a tally beside the label */
  count?: ReactNode;
  /** a sentence beside the label */
  note?: ReactNode;
  /** a note pushed to the right edge */
  endNote?: ReactNode;
  /** the zone's route out — `<button className="zone-route">Signals →</button>` */
  route?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('zone-head', className)}>
      <span className="u-label">{label}</span>
      {count != null && <span className="zone-count">{count}</span>}
      {note != null && <span className="zone-note">{note}</span>}
      {children}
      {endNote != null && <span className="zone-note zone-note--end">{endNote}</span>}
      {route}
    </div>
  );
}

// ── the instrument rail ─────────────────────────────────────────────────────
export function Rail({
  className, railClassName, children, after,
}: {
  className?: string;
  railClassName?: string;
  /** EXACTLY five RailCells. The rail is a fixed instrument, not a flexible list. */
  children: ReactNode;
  /** extra bands inside the same sheet (a filter bar, a notice) — use .instr-band */
  after?: ReactNode;
}) {
  return (
    <div className={cx('instr', className)}>
      <div className={cx('rail', railClassName)}>{children}</div>
      {after}
    </div>
  );
}

/**
 * The tone of a cell's figure. TEXT COLOUR, never a category hue.
 *   ok       the condition is met
 *   attend   it wants the operator
 *   broken   it is late, failed, or contradicted
 *   nil      counted, and the count is zero
 *   unknown  NOBODY COLLECTED IT. Not zero. Render the value as "not collected".
 */
export type RailTone = 'ink' | 'ok' | 'attend' | 'broken' | 'nil' | 'unknown';

export function RailCell({
  label, go, value, unit, tone, detail, onClick, pressed, disabled, title, valueTitle, className, children,
}: {
  label: ReactNode;
  /** the hover hint at the label row's right edge ('→', 'filter') */
  go?: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: RailTone;
  /** the line under the figure. NEVER blank — say what the figure is made of. */
  detail?: ReactNode;
  onClick?: () => void;
  /** for cells that FILTER rather than route — the cell stays pressed */
  pressed?: boolean;
  disabled?: boolean;
  title?: string;
  valueTitle?: string;
  className?: string;
  children?: ReactNode;
}) {
  const inner = (
    <>
      <span className="fl u-label">{label}{go != null && <span className="go" aria-hidden="true">{go}</span>}</span>
      <span className={cx('fv', 'voice-figure', tone && tone !== 'ink' && `fv--${tone}`)} title={valueTitle}>
        {value}
        {unit != null && <span className="u">{unit}</span>}
      </span>
      <span className="fd">{detail ?? ' '}</span>
      {children}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={cx('fld', className)} onClick={onClick} aria-pressed={pressed} disabled={disabled} title={title}>
        {inner}
      </button>
    );
  }
  return <div className={cx('fld', className)} title={title}>{inner}</div>;
}

// ── the sheet ───────────────────────────────────────────────────────────────
export function Sheet({
  className, clip, strong, edge, pad, style, children, ...rest
}: {
  className?: string;
  /** overflow hidden — rows must not escape the radius */
  clip?: boolean;
  strong?: boolean;
  /** the zone's ONE loud card: a 2px ink left edge. One per zone, at most. */
  edge?: boolean;
  pad?: boolean;
  style?: CSSProperties;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'children'>) {
  return (
    <div
      className={cx('sheet', clip && 'sheet--clip', strong && 'sheet--strong', edge && 'sheet--edge', pad && 'sheet--pad', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── the table ───────────────────────────────────────────────────────────────
/** `table.tbl`. Numeric columns take `className="num"` on BOTH the th and the td. */
export function Tbl({
  className, children, ...rest
}: { className?: string; children: ReactNode } & Omit<TableHTMLAttributes<HTMLTableElement>, 'className' | 'children'>) {
  return <table className={cx('tbl', className)} {...rest}>{children}</table>;
}

/** One option of a `FilterSelect`. The label is the vocabulary's own word — never a second spelling. */
export interface FilterOption {
  value: string;
  label: string;
}

/**
 * ONE CELL FILTER on a board's control bar (BUILD-BRIEF-R7 §6.2) — a labelled select whose
 * options are a vocabulary's own tokens, sitting inside an `.instr-band` beside the chips.
 *
 * A chip states a slice the page already holds; a filter asks the SERVER a question, so it wears
 * a field rather than a chip. `all` is the resting option and says so in words ("any"), because
 * a select showing a blank first line reads as "nothing collected" — which is a different fact.
 * The control fills in the moment it carries a token, the same way an active chip does.
 */
export function FilterSelect({ label, value, options, anyLabel, title, onChange }: {
  /** The cell's own name, already in the operator's language. */
  label: string;
  /** '' = no filter on this cell. */
  value: string;
  options: FilterOption[];
  /** The resting option's word — "any", in the operator's language. */
  anyLabel: string;
  title?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="filter-cell" title={title}>
      <span className="u-label">{label}</span>
      <select
        className={cx('filter-select', value && 'on')}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{anyLabel}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

// ── the atoms ───────────────────────────────────────────────────────────────

export type TagTone = 'neutral' | 'ok' | 'attend' | 'broken' | 'ghost';

/** A state word. `ghost` (dashed) is the shape a PROPOSED state takes. */
export function Tag({ tone = 'neutral', title, className, children }: {
  tone?: TagTone; title?: string; className?: string; children: ReactNode;
}) {
  return <span className={cx('tag', tone !== 'neutral' && `tag--${tone}`, className)} title={title}>{children}</span>;
}

/**
 * The claim mark. ◇ proposed · ▪ held · ❝ quoted.
 *
 * `proposed` for anything an agent or an import asserted that nobody has held yet; `held` for
 * what an operator committed to. A COMPUTED count carries no mark at all — do not decorate
 * arithmetic with a claim it never made.
 */
export function Att({ kind, title }: { kind: 'proposed' | 'held' | 'quoted'; title?: string }) {
  const label = kind === 'proposed' ? 'proposed, not held' : kind === 'held' ? 'held by the operator' : 'verbatim quote';
  return <span className={`att att--${kind}`} role="img" aria-label={label} title={title ?? label} />;
}

/** A verbatim quote with its source. The quote is never paraphrased and never trimmed silently. */
export function Ev({ children, src, className }: { children: ReactNode; src?: ReactNode; className?: string }) {
  return (
    <>
      <blockquote className={cx('ev', className)}>{children}</blockquote>
      {src != null && <div className="ev-src">{src}</div>}
    </>
  );
}

/** A text verb — the inline control. The accent means INTERACTION, never decoration. */
export function Lnk({
  onClick, href, disabled, title, className, children,
}: {
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  if (href && !disabled) {
    return <a className={cx('lnk', className)} href={href} title={title}>{children}</a>;
  }
  return (
    <button type="button" className={cx('lnk', className)} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

/** A quantity: a date, a count, a duration, money. Tabular sans — NEVER mono. */
export function Fig({ unit, title, className, children }: {
  unit?: ReactNode; title?: string; className?: string; children: ReactNode;
}) {
  return (
    <span className={cx('voice-figure', className)} title={title}>
      {children}
      {unit != null && <span className="u">{unit}</span>}
    </span>
  );
}

/** A human reference — ORG-0012, PER-0102, INT-0451. Mono, because it is an identity. */
export function Ref({ title, className, children }: { title?: string; className?: string; children: ReactNode }) {
  return <span className={cx('src-ref', className)} title={title}>{children}</span>;
}

/** Verbatim text from the record — serif, because it is someone's own words. */
export function Record({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx('voice-record', className)}>{children}</div>;
}

// ── lengths ─────────────────────────────────────────────────────────────────
//
// A quantity drawn as a LENGTH on a zero baseline is read faster and compared more exactly than
// the same figure in a column of digits: "our eyes compare the end points of the bars, so it is
// easy to see quickly which category is the biggest, which is the smallest, and also the
// incremental difference between categories" — and only from a zero baseline, "otherwise you
// get a false visual comparison" (Knaflic, Storytelling with Data, p. 50). Length and position
// carry quantity; hue does not ("which is greater — red or blue? — this isn't a meaningful
// question", p. 105). So a bar's DRESS is the claim's state, never a category hue: solid ink
// for a held reading, a dashed outline for a proposal nobody has held, a dotted fill for an
// estimate — the same three states ▪ ◇ and "estimated" carry in text.

/** How a bar's fill is dressed — the claim's state, never a category. */
export type BarFill = 'held' | 'proposed' | 'estimated' | 'computed';

/**
 * A length on a zero baseline. `value / max` is the fill; `rest="unknown"` draws the remainder of
 * `max` as a dotted track — the part of the whole nobody has accounted for — so an unknown
 * remainder is visible and is never mistaken for zero (P11). `value == null` draws no fill at
 * all: "not collected" is not a short bar. The figure beside it is `label`; the arithmetic that
 * produced a computed length goes in `title`.
 */
export function Bar({ value, max, fill = 'held', rest = 'none', title, label, className }: {
  value: number | null;
  max: number | null;
  fill?: BarFill;
  rest?: 'none' | 'unknown';
  title?: string;
  label?: ReactNode;
  className?: string;
}) {
  const pct = value != null && max != null && max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <span className={cx('bar', rest === 'unknown' && 'bar--rest', className)} title={title} role="img" aria-label={title}>
      <span className="bar-track">
        {value != null && max != null && max > 0 && (
          <span className={cx('bar-fill', `bar-fill--${fill}`)} style={{ width: `${pct}%` }} />
        )}
      </span>
      {label != null && <span className="bar-label">{label}</span>}
    </span>
  );
}

export interface StackSegment {
  key: string;
  value: number;
  fill?: BarFill;
  title?: string;
  label?: ReactNode;
}

/**
 * A 100 % stacked bar — parts of one whole on "a consistent baseline on both the far left and
 * the far right" (Knaflic p. 58), segments in the order the caller sorted them (largest first),
 * labelled directly inside when there is room (p. 96: "put the data labels right next to the
 * data they describe"). What the segments do not reach of `total` is the dotted remainder —
 * unattributed, and shown as such rather than dropped.
 */
export function Stack({ segments, total = 100, title, className }: {
  segments: StackSegment[];
  total?: number;
  title?: string;
  className?: string;
}) {
  const sum = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
  const scale = Math.max(total, sum) || 1;
  return (
    <span className={cx('stack', className)} title={title} role="img" aria-label={title}>
      {segments.map((seg) => {
        const w = (Math.max(0, seg.value) / scale) * 100;
        return (
          <span
            key={seg.key}
            className={cx('stack-seg', seg.fill && seg.fill !== 'held' && `stack-seg--${seg.fill}`)}
            style={{ width: `${w}%` }}
            title={seg.title}
          >
            {w >= 9 ? seg.label : null}
          </span>
        );
      })}
    </span>
  );
}
